import { useEffect, useMemo, useState } from 'react';
import { Search, CheckCircle2, Library } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { bytesToHuman } from './format';
import { fileMatchesAnyMimeGroup, normalizeMimeGroups } from './mimeGroups';
import { useFileLibraryFeatureEnabled } from './useFileLibraryFeature';
import { useFileLibraryFiles } from './useFileLibrary';
import {
  CompanyFilePreviewDialog,
  CompanyFileThumbnail,
  PreviewButton,
} from './CompanyFilePreview';
import type { CompanyFileDto, CompanyFileMimeGroup, FileLibrarySelection } from './types';

interface FileLibraryPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (file: FileLibrarySelection) => void;
  companyId?: string;
  batchId?: string;
  acceptMimeGroup?: CompanyFileMimeGroup;
  acceptMimeGroups?: CompanyFileMimeGroup[];
  acceptFile?: (file: CompanyFileDto) => boolean;
}

const toSelection = (file: CompanyFileDto): FileLibrarySelection => ({
  company_file_id: file.company_file_id,
  display_name: file.display_name,
  detected_mime_type: file.detected_mime_type,
  size_bytes: file.size_bytes,
  sha256: file.sha256,
});

export const FileLibraryPickerDialog = ({
  open,
  onOpenChange,
  onSelect,
  companyId,
  batchId,
  acceptMimeGroup,
  acceptMimeGroups,
  acceptFile,
}: FileLibraryPickerDialogProps) => {
  const { t } = useTranslation('fileLibrary');
  const enabled = useFileLibraryFeatureEnabled();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<CompanyFileDto | null>(null);
  const [previewFile, setPreviewFile] = useState<CompanyFileDto | null>(null);
  const [hoverPreviewFileId, setHoverPreviewFileId] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  useEffect(() => {
    setQ('');
    setCursor(undefined);
    setSelected(null);
    setPreviewFile(null);
    setHoverPreviewFileId(null);
  }, [acceptMimeGroup, acceptMimeGroups, batchId, companyId]);
  useEffect(() => {
    setCursor(undefined);
    setSelected(null);
    setHoverPreviewFileId(null);
  }, [acceptMimeGroup, acceptMimeGroups, batchId, companyId, q]);
  const acceptedGroups = useMemo(
    () => normalizeMimeGroups(acceptMimeGroups ?? acceptMimeGroup),
    [acceptMimeGroup, acceptMimeGroups],
  );
  const serverMimeGroup = acceptedGroups.length === 1 ? acceptedGroups[0] : 'all';
  useEffect(() => {
    if (!open) {
      setQ('');
      setCursor(undefined);
      setSelected(null);
      setPreviewFile(null);
      setHoverPreviewFileId(null);
    }
  }, [open]);
  const params = useMemo(
    () => ({
      ...(batchId ? { batch_id: batchId } : companyId ? { company_id: companyId } : {}),
      q,
      mime_group: serverMimeGroup,
      status: 'ready' as const,
      cursor,
    }),
    [batchId, companyId, cursor, q, serverMimeGroup],
  );
  const files = useFileLibraryFiles(params, open && enabled);
  const visibleFiles = useMemo(
    () =>
      (files.data?.files ?? [])
        .filter((file) => fileMatchesAnyMimeGroup(file, acceptedGroups))
        .filter((file) => (acceptFile ? acceptFile(file) : true)),
    [acceptFile, acceptedGroups, files.data?.files],
  );

  const choose = () => {
    if (!selected) return;
    onSelect(toSelection(selected));
    onOpenChange(false);
    setSelected(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl"
        onEscapeKeyDown={(event) => {
          if (hoverPreviewFileId) {
            event.preventDefault();
            setHoverPreviewFileId(null);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('efs.file_library.picker.title')}</DialogTitle>
          <DialogDescription>{t('efs.file_library.picker.subtitle')}</DialogDescription>
        </DialogHeader>
        {!enabled ? (
          <div className="rounded-md border border-efs-border bg-efs-bg-subtle p-4 text-sm text-efs-text-muted">
            {t('efs.file_library.feature_disabled')}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-efs-text-muted" />
              <Input
                value={q}
                onChange={(event) => setQ(event.target.value)}
                className="pl-9"
                placeholder={t('efs.file_library.filters.search_placeholder')}
              />
            </div>
            <div className="max-h-[26rem] overflow-y-auto rounded-md border border-efs-border">
              {files.isLoading ? (
                <div className="p-6 text-sm text-efs-text-muted">
                  {t('efs.file_library.loading')}
                </div>
              ) : visibleFiles.length === 0 ? (
                <div className="p-6 text-sm text-efs-text-muted">{t('efs.file_library.empty')}</div>
              ) : (
                <ul className="divide-y divide-efs-border">
                  {visibleFiles.map((file) => {
                    const active = selected?.company_file_id === file.company_file_id;
                    return (
                      <li key={file.company_file_id}>
                        <div
                          className={`flex items-center gap-3 px-4 py-3 ${
                            active ? 'bg-efs-bg-subtle ring-1 ring-inset ring-efs-accent' : ''
                          }`}
                        >
                          <CompanyFileThumbnail
                            file={file}
                            onPreview={() => setPreviewFile(file)}
                          />
                          <button
                            type="button"
                            onClick={() => setSelected(file)}
                            className="min-w-0 flex-1 rounded text-left focus:outline-none focus:ring-2 focus:ring-efs-accent"
                            aria-pressed={active}
                          >
                            <span className="block truncate text-sm font-medium text-efs-text-primary">
                              {file.display_name}
                            </span>
                            <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-efs-text-muted">
                              <Library className="h-3.5 w-3.5" aria-hidden="true" />
                              {file.detected_mime_type ??
                                t('efs.file_library.preview.unknown_type')}
                              <span aria-hidden="true">·</span>
                              {bytesToHuman(file.size_bytes)}
                              <span className="font-mono">{file.sha256.slice(0, 12)}</span>
                            </span>
                          </button>
                          <div className="flex shrink-0 items-center gap-2">
                            <PreviewButton
                              file={file}
                              onPreview={() => setPreviewFile(file)}
                              hoverOpen={hoverPreviewFileId === file.company_file_id}
                              onHoverOpenChange={(nextOpen) =>
                                setHoverPreviewFileId(nextOpen ? file.company_file_id : null)
                              }
                              compact
                            />
                            {active ? (
                              <Badge className="shrink-0">
                                <CheckCircle2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                                {t('efs.file_library.picker.selected')}
                              </Badge>
                            ) : null}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            {files.data?.next_cursor ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setCursor(files.data?.next_cursor ?? undefined)}
              >
                {t('efs.file_library.actions.next_page')}
              </Button>
            ) : null}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button type="button" onClick={choose} disabled={!selected || !enabled}>
            {t('efs.file_library.picker.choose')}
          </Button>
        </DialogFooter>
      </DialogContent>
      <CompanyFilePreviewDialog
        file={previewFile}
        open={Boolean(previewFile)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPreviewFile(null);
        }}
      />
    </Dialog>
  );
};
